import { Routes } from '@angular/router';
import { authGuard, guestGuard, permissionGuard } from './core/auth/auth.guards';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { environment } from '../environments/environment';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/login/login').then((m) => m.Login),
  },
  {
    // 비밀번호 찾기 — 로그인 ID+등록 이메일로 임시 비밀번호를 메일로 받는다(1안). 관리자 초기화 안내는 보조 문단
    path: 'find-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/login/find-password-page').then((m) => m.FindPasswordPage),
  },
  // 공용 UI 쇼케이스 — 인증 밖 공개 라우트 (API 무호출 정적 데모, 리뷰·검수용).
  // 운영 빌드(`environment.ts`)는 플래그가 꺼져 있어 라우트가 배열에 들어가지 않는다. 지연 청크 파일은
  // 산출물에 남지만(esbuild가 타 모듈의 객체 속성을 상수 접기하지 않는다) 라우트가 없어 내려받지 않는다.
  ...(environment.styleguideEnabled
    ? [
        {
          path: 'styleguide',
          data: { title: '스타일 가이드' },
          loadComponent: () =>
            import('./features/styleguide/styleguide-page').then((m) => m.StyleguidePage),
        },
      ]
    : []),
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./shell/shell').then((m) => m.Shell),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'main' },
      {
        // 메인 (menuCode MAIN) — 공지 위젯 + 가맹점 운영현황 드릴다운
        path: 'main',
        canActivate: [permissionGuard('MAIN')],
        loadComponent: () => import('./features/main/main-page').then((m) => m.MainPage),
      },
      {
        // 업체관리 (menuCode ORG) — 공통 그리드 프레임워크 첫 적용 화면
        path: 'organizations',
        canActivate: [permissionGuard('ORG')],
        loadComponent: () => import('./features/orgs/orgs-page').then((m) => m.OrgsPage),
      },
      {
        // 사용자관리 (menuCode USER)
        path: 'users',
        canActivate: [permissionGuard('USER')],
        loadComponent: () => import('./features/users/users-page').then((m) => m.UsersPage),
      },
      {
        // 공지사항 관리 (menuCode NOTICE)
        path: 'notices',
        canActivate: [permissionGuard('NOTICE')],
        loadComponent: () => import('./features/notices/notices-page').then((m) => m.NoticesPage),
      },
      {
        // 모델등록 (menuCode MODEL)
        path: 'models',
        canActivate: [permissionGuard('MODEL')],
        loadComponent: () =>
          import('./features/products/products-page').then((m) => m.ProductsPage),
      },
      {
        // 제품조회/등록 (menuCode PRODUCT) — 시리얼 등록·배정 카드 포함, 이탈 시 미저장 확인
        path: 'products',
        canActivate: [permissionGuard('PRODUCT')],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./features/devices/devices-page').then((m) => m.DevicesPage),
      },
      {
        // 시리얼등록 메뉴(menuCode SERIAL)는 제품조회/등록 화면의 신규등록 카드로 통합
        path: 'serials',
        redirectTo: 'products',
      },
      {
        // 가맹점조회/등록 (menuCode MERCHANT) — 목록↔상세는 ?id= 쿼리로 전환.
        // 셸 메뉴·탭 재클릭은 같은 라우트로 쿼리만 바뀌어 컴포넌트가 재사용되므로,
        // 쿼리 변경에도 canDeactivate가 다시 돌게 해야 상세 입력이 확인 없이 사라지지 않는다
        path: 'merchants',
        canActivate: [permissionGuard('MERCHANT')],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/merchants/merchants-page').then((m) => m.MerchantsPage),
      },
      {
        // PAGE관리 (menuCode PAGE) — 계층×메뉴 노출 매트릭스. 저장 전 이탈 시 확인
        path: 'pages',
        canActivate: [permissionGuard('PAGE')],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./features/pages/pages-page').then((m) => m.PagesPage),
      },
      {
        // A/S 및 이력조회 (menuCode AS) — 목록↔상세는 가맹점 화면과 같은 ?id= 쿼리 전환.
        // 쿼리 변경에도 canDeactivate가 다시 돌아야 접수 입력이 확인 없이 사라지지 않는다
        path: 'as-history',
        canActivate: [permissionGuard('AS_HISTORY')],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/as-history/as-history-page').then((m) => m.AsHistoryPage),
      },
      {
        // 펌웨어 배포 (menuCode DEPLOY) — 본사 전용. 고른 대상·변경후 버전을 두고 나가면 지시가 사라진다
        path: 'deploys',
        canActivate: [permissionGuard('DEPLOY')],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./features/deploys/deploys-page').then((m) => m.DeploysPage),
      },
      {
        // 통신로그 조회 (menuCode COMM_LOG) — 전문 단위 TCP 기록. 본사 전용(사용자 결정 2026-09-13),
        // 서버 권한 정의도 VIEW 하나뿐이라 입력이 없다 → 이탈 확인 가드가 필요 없다
        path: 'comm-logs',
        canActivate: [permissionGuard('COMM_LOG')],
        loadComponent: () =>
          import('./features/comm-logs/comm-logs-page').then((m) => m.CommLogsPage),
      },
      {
        // 감사 로그 조회 (menuCode AUDIT_LOG) — 관리자 작업 이력. 본사 전용, 서버 권한 정의도
        // VIEW 하나뿐이라 입력이 없다 → 이탈 확인 가드가 필요 없다. 상세는 행 아래 확장(라우트 없음)
        path: 'audit-logs',
        canActivate: [permissionGuard('AUDIT_LOG')],
        loadComponent: () =>
          import('./features/audit-logs/audit-logs-page').then((m) => m.AuditLogsPage),
      },
      {
        // 통계 (menuCode STAT) — 기본 집계 4탭 (설치·버전·배포결과·장애)
        path: 'stats',
        canActivate: [permissionGuard('STAT')],
        loadComponent: () => import('./features/stats/stats-page').then((m) => m.StatsPage),
      },
      {
        // 펌웨어등록 메뉴(menuCode FIRMWARE)는 모델등록 화면의 펌웨어 등록 카드로 통합
        path: 'firmwares',
        redirectTo: 'models',
      },
      {
        // 리더기 설정관리 메뉴(menuCode READER_SETTING)는 가맹점 상세의 설정 패널로 통합
        path: 'reader-settings',
        redirectTo: 'merchants',
      },
      {
        // 모든 메뉴가 실제 컴포넌트에 연결돼 있어 여기 오는 것은 오타 URL뿐이다 — 404
        path: '**',
        loadComponent: () => import('./shell/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
